/**
 * scheduleStatHolidays.ts
 * Frontend service for the StatHoliday API (Phase 1, Item 7).
 *
 * All requests go through apiClient (MSAL auth + error capture).
 * Endpoints:
 *   GET    /api/stat-holidays            — list (optional ?year=, ?region=)
 *   GET    /api/stat-holidays/:key       — single
 *   POST   /api/stat-holidays            — create
 *   PUT    /api/stat-holidays/:key       — update
 *   DELETE /api/stat-holidays/:key       — soft delete
 */

import apiClient from './apiClient';
import type { StatHoliday } from '../types/schedule';

export interface CreateStatHolidayPayload {
  HolidayDate: string; // 'YYYY-MM-DD'
  Name: string;
  Region?: string;     // default 'BC'
}

export interface UpdateStatHolidayPayload {
  HolidayDate?: string;
  Name?: string;
  Region?: string;
}

export async function fetchStatHolidays(opts: {
  year?: number;
  region?: string;
} = {}): Promise<StatHoliday[]> {
  const params: Record<string, string> = {};
  if (opts.year !== undefined) params.year = String(opts.year);
  if (opts.region !== undefined) params.region = opts.region;
  const response = await apiClient.get<StatHoliday[]>('/stat-holidays', { params });
  return response.data;
}

export async function fetchStatHoliday(key: string): Promise<StatHoliday> {
  const response = await apiClient.get<StatHoliday>(`/stat-holidays/${key}`);
  return response.data;
}

export async function createStatHoliday(data: CreateStatHolidayPayload): Promise<StatHoliday> {
  const response = await apiClient.post<StatHoliday>('/stat-holidays', data);
  return response.data;
}

export async function updateStatHoliday(
  key: string,
  data: UpdateStatHolidayPayload,
): Promise<StatHoliday> {
  const response = await apiClient.put<StatHoliday>(`/stat-holidays/${key}`, data);
  return response.data;
}

export async function deleteStatHoliday(key: string): Promise<void> {
  await apiClient.delete(`/stat-holidays/${key}`);
}
